import { FC, memo, useState } from 'react';
import {
  FULL_DATE_FORMAT,
  FULL_DATE_WITHOUT_YEAR_FORMAT
} from 'constants/dates';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { VideoCameraOutlined } from '@ant-design/icons';
import { useCallButton } from 'hooks';
import {
  convertSecondsToMMSS,
  JOIN_TIMER_SECONDS
} from '@medicusunion/front-utils';
import { App } from 'antd';
import copy from 'copy-to-clipboard';
import { openAppointmentRoom } from 'services/api/appointments';
import { createMeetingRoom } from 'services/api/meetings';

import { StyledButton, StyledCallCounter } from './styles';
import { ICallButtonProps } from './types';

dayjs.extend(customParseFormat);

const CallButton: FC<ICallButtonProps> = ({
  date,
  appointmentId,
  onJoinClick
}) => {
  const { t } = useTranslation();

  const { message } = App.useApp();

  const [loading, setLoading] = useState(false);

  const seconds = useCallButton(date);

  const onCopyLinkAndPassword = async () => {
    try {
      setLoading(true);

      if (!appointmentId) throw new Error('Missing appointmentId');

      const [room, meeting] = await Promise.all([
        openAppointmentRoom(appointmentId),
        createMeetingRoom(appointmentId)
      ]);

      if (!room?.redirect_url || !room.meetUrl) {
        throw new Error('No link');
      }

      const password = meeting?.password;

      if (!password) {
        throw new Error('No password');
      }

      const url = `${room.redirect_url}/home/${room.meetUrl}`;

      copy(`🔗 Meeting Link: ${url}\n🔒 Password: ${password}`);

      message.success('Link and password copied to clipboard');
    } catch (err) {
      message.error('Failed to copy link and password. Please contact IT team');
    } finally {
      setLoading(false);
    }
  };

  if (seconds <= JOIN_TIMER_SECONDS) {
    const secondsFormatted =
      seconds > 0 ? ` (${convertSecondsToMMSS(seconds)})` : '';

    return (
      <StyledButton
        type="primary"
        key="join-call"
        size="middle"
        style={{ display: 'flex', alignItems: 'center' }}
        onClick={onJoinClick}
        loading={loading}
        placement="bottomRight"
        menu={{
          items: [
            {
              label: 'Copy link and password',
              key: 'copy-link-and-password',
              disabled: loading,
              onClick: onCopyLinkAndPassword
            }
          ]
        }}
      >
        <VideoCameraOutlined style={{ fontSize: 16 }} />
        {t('appointments_details.join_call')}
        {secondsFormatted}
      </StyledButton>
    );
  }

  const formattedDate = dayjs(date, FULL_DATE_FORMAT).format(
    FULL_DATE_WITHOUT_YEAR_FORMAT
  );

  return (
    <StyledCallCounter>
      {t('appointments_details.call_starts_in')} {formattedDate}
    </StyledCallCounter>
  );
};

export default memo(CallButton);
